import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { GetActivityResponseType, VoteTransactionActivity } from '@/entities';
import { telegramClientApi } from '@/shared';

interface ActivityState {
  items: VoteTransactionActivity[];
  count: number;
  page: number;
  loading: boolean;
  error: string | null;
}

const initialState: ActivityState = {
  items: [],
  count: 0,
  page: 1,
  loading: false,
  error: null
};

export const fetchActivity = createAsyncThunk<
  { items: VoteTransactionActivity[]; count: number; page: number; append?: boolean },
  { page?: number; pageSize?: number; append?: boolean } | void,
  { rejectValue: string }
>('activity/getVotes', async (args, { rejectWithValue }) => {
  try {
    const { page = 1, pageSize = 20, append = false } = (args as any) ?? {};
    const res = await telegramClientApi.getVotesActivity(page, pageSize);
    if (res.error) throw new Error(res.error.message);

    const payload = res.data as GetActivityResponseType;
    const items = payload?.data ?? [];
    const count = payload?.count ?? 0;
    const currentPage = (payload as any)?.page ?? page;

    return { items, count, page: currentPage, append };
  } catch (e: any) {
    return rejectWithValue(e?.message ?? 'Failed to fetch votes activity');
  }
});

const activitySlice = createSlice({
  name: 'activity',
  initialState,
  reducers: {
    resetActivity: () => initialState,
    clearActivityError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActivity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActivity.fulfilled, (state, { payload }) => {
        state.loading = false;
        if (payload.append && payload.page > 1) {
          const existingIds = new Set(state.items.map((i) => i.id));
          const merged = [...state.items];
          for (const item of payload.items) {
            if (!existingIds.has(item.id)) merged.push(item);
          }
          state.items = merged;
        } else {
          state.items = payload.items;
        }
        state.count = payload.count;
        state.page = payload.page;
      })
      .addCase(fetchActivity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to fetch votes activity';
      });
  }
});

export const { resetActivity, clearActivityError } = activitySlice.actions;

export default activitySlice.reducer;
